import React, { useState, useEffect, useCallback } from 'react';
import {
  FiUsers,
  FiSearch,
  FiFilter,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiEye,
  FiUserCheck,
  FiUserX,
  FiRefreshCw,
  FiDownload,
  FiMoreVertical,
  FiShield,
  FiMail,
  FiPhone
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { AdminLayout } from '../../components/admin';
import { useAdminData } from '../../hooks/admin/useAdminData';
import { useAdminAuth } from '../../hooks/admin/useAdminAuth';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import EditUserModal from '../../components/admin/users/EditUserModal';
import { useDialog } from '../../hooks/useDialog';
import { API_URL } from '../../services/api';
import { USER_CATEGORIES, getUserCategoryLabel, getUserCategoryIcon } from '../../constants/userCategories';

// Types
interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  userCategory?: string;
  role: 'USER' | 'COMPANY_OWNER' | 'ADMIN' | 'SUPER_ADMIN';
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'BANNED';
  createdAt: string;
  lastLoginAt?: string;
  companyCount?: number;
}

interface UserFilters {
  search: string;
  role: string;
  status: string;
  category: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

interface UserListResponse {
  users: User[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

const UserManagement: React.FC = () => {
  const { token } = useAdminAuth();
  const [errorState, { handleError, clearError }] = useErrorHandler();
  const { showConfirm, ConfirmDialog, AlertDialog } = useDialog();

  // Custom API call function
  const callApi = useCallback(async <T = any>(options: {
    method: 'GET' | 'POST' | 'PUT' | 'DELETE';
    endpoint: string;
    data?: any;
  }): Promise<{ success: boolean; data: T; message?: string }> => {
    if (!token) {
      throw new Error('ไม่พบ token สำหรับการยืนยันตัวตน');
    }

    
    const response = await fetch(`${API_URL}/api/admin${options.endpoint}`, {
      method: options.method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: options.data ? JSON.stringify(options.data) : undefined,
    });

    const result = await response.json();

    if (!response.ok) {
      // Extract error message: priority to detail > message > HTTP status
      const errorMessage =
        result.detail ||
        result.error?.details ||
        result.error?.message ||
        result.message ||
        `HTTP ${response.status}`;
      throw new Error(errorMessage);
    }

    return {
      success: true,
      data: result.data || result,
      message: result.message
    };
  }, [token]);

  // State
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalUsers, setTotalUsers] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  // Filters
  const [filters, setFilters] = useState<UserFilters>({
    search: '',
    role: '',
    status: '',
    category: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  // Load users
  const loadUsers = useCallback(async (page = 1) => {
    try {
      setLoading(true);

      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...(filters.search && { search: filters.search }),
        ...(filters.role && { role: filters.role }),
        ...(filters.status && { status: filters.status }),
        ...(filters.category && { category: filters.category }),
        sort_by: filters.sortBy,
        sort_order: filters.sortOrder
      });

      const response = await callApi<UserListResponse>({
        method: 'GET',
        endpoint: `/users?${params.toString()}`
      });

      if (response.success) {
        console.log('API Response:', response.data); // Debug log
        setUsers(response.data.users);
        setTotalUsers(response.data.total);
        setCurrentPage(response.data.page);
        setTotalPages(response.data.total_pages);
        console.log('Pagination state:', {
          currentPage: response.data.page,
          totalPages: response.data.total_pages,
          total: response.data.total,
          has_next: response.data.has_next,
          has_prev: response.data.has_prev
        }); // Debug log
      }
    } catch (error) {
      handleError(error, 'ไม่สามารถโหลดรายการผู้ใช้ได้');
    } finally {
      setLoading(false);
    }
  }, [filters, token, callApi, handleError]);

  // Initial load
  useEffect(() => {
    if (token) {
      loadUsers(1);
    }
  }, [loadUsers, token]);

  // Handle user action
  const handleUserAction = async (userId: string, action: 'activate' | 'deactivate' | 'delete', user?: User) => {
    try {
      let endpoint = '';
      let method: 'POST' | 'PUT' | 'DELETE' = 'POST';
      let message = '';
      let confirmMessage = '';
      let confirmTitle = '';

      switch (action) {
        case 'activate':
          endpoint = `/users/${userId}/activate`;
          message = 'เปิดใช้งานผู้ใช้เรียบร้อยแล้ว';
          break;
        case 'deactivate':
          endpoint = `/users/${userId}/deactivate`;
          message = 'ปิดใช้งานผู้ใช้เรียบร้อยแล้ว';
          break;
        case 'delete':
          confirmTitle = 'ยืนยันการลบผู้ใช้';
          confirmMessage = `ต้องการลบผู้ใช้ ${user?.email} หรือไม่?\n\nการดำเนินการนี้จะทำการ soft delete ผู้ใช้และไม่สามารถย้อนกลับได้`;

          await showConfirm(
            {
              title: confirmTitle,
              message: confirmMessage,
              confirmText: 'ลบผู้ใช้',
              cancelText: 'ยกเลิก',
              variant: 'danger'
            },
            async () => {
              const response = await callApi({
                method: 'DELETE',
                endpoint: `/users/${userId}`
              });

              if (response.success) {
                toast.success('ลบผู้ใช้เรียบร้อยแล้ว');
                loadUsers(currentPage);
              }
            }
          );
          return;
      }

      const response = await callApi({
        method,
        endpoint
      });

      if (response.success) {
        toast.success(message);
        loadUsers(currentPage);
      }
    } catch (error: any) {
      // Display error message directly without adding prefix
      toast.error(error.message || 'ไม่สามารถดำเนินการได้');
    }
  };

  // Handle role change
  const handleRoleChange = async (userId: string, newRole: string) => {
    try {
      const response = await callApi({
        method: 'PUT',
        endpoint: `/users/${userId}/role`,
        data: { role: newRole }
      });

      if (response.success) {
        toast.success('เปลี่ยนบทบาทผู้ใช้เรียบร้อยแล้ว');
        loadUsers(currentPage);
      }
    } catch (error) {
      handleError(error, 'ไม่สามารถเปลี่ยนบทบาทได้');
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedUsers.length === 0) {
      toast.error('กรุณาเลือกผู้ใช้ที่ต้องการลบ');
      return;
    }

    const confirmTitle = 'ยืนยันการลบผู้ใช้';
    const confirmMessage = `ต้องการลบผู้ใช้ ${selectedUsers.length} รายการหรือไม่?\n\nการดำเนินการนี้จะทำการ soft delete และไม่สามารถย้อนกลับได้`;

    await showConfirm(
      {
        title: confirmTitle,
        message: confirmMessage,
        confirmText: 'ลบผู้ใช้',
        cancelText: 'ยกเลิก',
        variant: 'danger'
      },
      async () => {
        try {
          const response = await callApi<{
            success: boolean;
            message: string;
            successful: string[];
            failed: Array<{ id: string; email?: string; reason: string }>;
            summary: { total_requested: number; successful: number; failed: number };
          }>({
            method: 'POST',
            endpoint: '/users/bulk-delete',
            data: {
              action: 'delete',
              entity_ids: selectedUsers
            }
          });

          if (response.success) {
            const { successful, failed, summary } = response.data;

            // Show success toast
            if (summary.successful > 0) {
              toast.success(`ลบผู้ใช้สำเร็จ ${summary.successful} รายการ`);
            }

            // Show failed items if any
            if (summary.failed > 0) {
              toast.error(`ลบไม่สำเร็จ ${summary.failed} รายการ`);

              // Show detailed error for each failed item
              failed.forEach((item) => {
                const userEmail = item.email || item.id;
                toast.error(`${userEmail}: ${item.reason}`, { duration: 5000 });
              });
            }

            // Clear selection and reload
            setSelectedUsers([]);
            loadUsers(currentPage);
          }
        } catch (error: any) {
          toast.error(error.message || 'ไม่สามารถลบผู้ใช้ได้');
        }
      }
    );
  };

  // Handle deselect all
  const handleDeselectAll = () => {
    setSelectedUsers([]);
  };

  // Excel Export function
  const exportToExcel = () => {
    try {
      // Prepare data for export
      const exportData = users.map((user, index) => ({
        'ลำดับ': index + 1,
        'ชื่อ-นามสกุล': user.firstName && user.lastName
          ? `${user.firstName} ${user.lastName}`
          : 'ไม่ระบุชื่อ',
        'อีเมล': user.email,
        'เบอร์โทร': user.phone || '-',
        'ประเภทผู้ใช้': user.userCategory
          ? getUserCategoryLabel(user.userCategory)
          : 'ไม่ระบุ',
        'บทบาท': getRoleLabel(user.role),
        'สถานะ': getStatusLabel(user.status),
        'วันที่สมัคร': new Date(user.createdAt).toLocaleDateString('th-TH'),
        'เข้าสู่ระบบล่าสุด': user.lastLoginAt
          ? new Date(user.lastLoginAt).toLocaleDateString('th-TH')
          : 'ยังไม่เคยเข้าสู่ระบบ'
      }));

      // Create CSV content
      const headers = Object.keys(exportData[0]);
      const csvContent = [
        headers.join(','),
        ...exportData.map(row =>
          headers.map(header => {
            const value = row[header as keyof typeof row];
            // Escape commas and quotes in values
            return `"${String(value).replace(/"/g, '""')}"`;
          }).join(',')
        )
      ].join('\n');

      // Add BOM for Thai language support
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('ส่งออกข้อมูลสำเร็จ');
    } catch (error) {
      toast.error('ไม่สามารถส่งออกข้อมูลได้');
      console.error('Export error:', error);
    }
  };

  // Helper functions for labels
  const getRoleLabel = (role: string): string => {
    const labels: Record<string, string> = {
      'SUPER_ADMIN': 'ผู้ดูแลระบบสูงสุด',
      'ADMIN': 'ผู้ดูแลระบบ',
      'COMPANY_OWNER': 'เจ้าของบริษัท',
      'USER': 'ผู้ใช้ทั่วไป'
    };
    return labels[role] || role;
  };

  const getStatusLabel = (status: string): string => {
    const labels: Record<string, string> = {
      'ACTIVE': 'ใช้งานได้',
      'INACTIVE': 'ไม่ใช้งาน',
      'SUSPENDED': 'ถูกระงับ',
      'BANNED': 'ถูกแบน'
    };
    return labels[status] || status;
  };

  // Role badge component
  const RoleBadge: React.FC<{ role: string }> = ({ role }) => {
    const colors = {
      'SUPER_ADMIN': 'bg-red-100 text-red-800',
      'ADMIN': 'bg-purple-100 text-purple-800',
      'COMPANY_OWNER': 'bg-blue-100 text-blue-800',
      'USER': 'bg-gray-100 text-gray-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[role as keyof typeof colors] || colors.USER}`}>
        {getRoleLabel(role)}
      </span>
    );
  };

  // Status badge component
  const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const colors = {
      'ACTIVE': 'bg-green-100 text-green-800',
      'INACTIVE': 'bg-gray-100 text-gray-800',
      'SUSPENDED': 'bg-yellow-100 text-yellow-800',
      'BANNED': 'bg-red-100 text-red-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[status as keyof typeof colors] || colors.INACTIVE}`}>
        {getStatusLabel(status)}
      </span>
    );
  };

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center">
                <FiUsers className="mr-2 sm:mr-3 h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
                จัดการผู้ใช้
              </h1>
              <p className="mt-1 text-xs sm:text-sm text-gray-600">
                จัดการข้อมูลผู้ใช้งานและสิทธิ์การเข้าถึง (รวม {totalUsers.toLocaleString()} คน)
              </p>
            </div>
            <div className="flex flex-wrap gap-2 sm:space-x-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-3 sm:px-4 py-2 border border-gray-300 shadow-sm text-xs sm:text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <FiFilter className="mr-1 sm:mr-2 h-4 w-4" />
                <span className="hidden sm:inline">ตัวกรอง</span>
                <span className="sm:hidden">กรอง</span>
              </button>
              <button
                onClick={exportToExcel}
                disabled={users.length === 0}
                className="inline-flex items-center px-3 sm:px-4 py-2 border border-gray-300 shadow-sm text-xs sm:text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FiDownload className="mr-1 sm:mr-2 h-4 w-4" />
                <span className="hidden sm:inline">ส่งออก Excel</span>
                <span className="sm:hidden">Excel</span>
              </button>
              <button
                onClick={() => loadUsers(currentPage)}
                className="inline-flex items-center px-3 sm:px-4 py-2 border border-gray-300 shadow-sm text-xs sm:text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <FiRefreshCw className="mr-1 sm:mr-2 h-4 w-4" />
                รีเฟรช
              </button>
              <button
                onClick={() => setShowUserModal(true)}
                className="inline-flex items-center px-3 sm:px-4 py-2 border border-transparent shadow-sm text-xs sm:text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <FiPlus className="mr-1 sm:mr-2 h-4 w-4" />
                <span className="hidden sm:inline">เพิ่มผู้ใช้</span>
                <span className="sm:hidden">เพิ่ม</span>
              </button>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="mb-6 space-y-4">
          {/* Search */}
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="ค้นหาด้วยอีเมล, ชื่อ, หรือเบอร์โทร..."
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            />
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="bg-gray-50 p-4 rounded-lg space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">บทบาท</label>
                  <select
                    className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white"
                    value={filters.role}
                    onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value }))}
                  >
                    <option value="">ทั้งหมด</option>
                    <option value="USER">ผู้ใช้ทั่วไป</option>
                    <option value="COMPANY_OWNER">เจ้าของบริษัท</option>
                    <option value="ADMIN">ผู้ดูแลระบบ</option>
                    <option value="SUPER_ADMIN">ผู้ดูแลระบบสูงสุด</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ประเภทผู้ใช้</label>
                  <select
                    className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white"
                    value={filters.category}
                    onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                  >
                    <option value="">ทั้งหมด</option>
                    {USER_CATEGORIES.map((category) => (
                      <option key={category.value} value={category.value}>
                        {category.icon} {category.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">สถานะ</label>
                  <select
                    className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white"
                    value={filters.status}
                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                  >
                    <option value="">ทั้งหมด</option>
                    <option value="ACTIVE">ใช้งานได้</option>
                    <option value="INACTIVE">ไม่ใช้งาน</option>
                    <option value="SUSPENDED">ถูกระงับ</option>
                    <option value="BANNED">ถูกแบน</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">เรียงตาม</label>
                  <select
                    className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white"
                    value={`${filters.sortBy}-${filters.sortOrder}`}
                    onChange={(e) => {
                      const [sortBy, sortOrder] = e.target.value.split('-');
                      setFilters(prev => ({ ...prev, sortBy, sortOrder: sortOrder as 'asc' | 'desc' }));
                    }}
                  >
                    <option value="createdAt-desc">วันที่สมัคร (ใหม่สุด)</option>
                    <option value="createdAt-asc">วันที่สมัคร (เก่าสุด)</option>
                    <option value="lastLoginAt-desc">เข้าสู่ระบบล่าสุด</option>
                    <option value="email-asc">อีเมล (A-Z)</option>
                    <option value="email-desc">อีเมล (Z-A)</option>
                  </select>
                </div>
              </div>

              {/* Active Filter Chips */}
              {(filters.role || filters.category || filters.status) && (
                <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-200">
                  <span className="text-sm text-gray-600">ตัวกรองที่ใช้งาน:</span>
                  {filters.role && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      บทบาท: {filters.role}
                      <button
                        onClick={() => setFilters(prev => ({ ...prev, role: '' }))}
                        className="ml-1 hover:text-blue-900"
                      >
                        ×
                      </button>
                    </span>
                  )}
                  {filters.category && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      {getUserCategoryIcon(filters.category)} {getUserCategoryLabel(filters.category)}
                      <button
                        onClick={() => setFilters(prev => ({ ...prev, category: '' }))}
                        className="ml-1 hover:text-purple-900"
                      >
                        ×
                      </button>
                    </span>
                  )}
                  {filters.status && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      สถานะ: {filters.status}
                      <button
                        onClick={() => setFilters(prev => ({ ...prev, status: '' }))}
                        className="ml-1 hover:text-green-900"
                      >
                        ×
                      </button>
                    </span>
                  )}
                  <button
                    onClick={() => setFilters(prev => ({ ...prev, role: '', category: '', status: '' }))}
                    className="text-xs text-gray-600 hover:text-gray-900 underline"
                  >
                    ล้างตัวกรองทั้งหมด
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bulk Action Toolbar */}
        {selectedUsers.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 animate-fadeIn">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center space-x-4">
                <span className="text-sm font-medium text-blue-900">
                  เลือกแล้ว {selectedUsers.length} รายการ
                </span>
                <button
                  onClick={handleDeselectAll}
                  className="text-sm text-blue-700 hover:text-blue-900 underline"
                >
                  ยกเลิกการเลือก
                </button>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleBulkDelete}
                  className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-md shadow-sm transition-colors"
                >
                  <FiTrash2 className="mr-2 h-4 w-4" />
                  ลบที่เลือก ({selectedUsers.length})
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <FiRefreshCw className="animate-spin h-8 w-8 text-blue-600" />
              <span className="ml-2 text-gray-600">กำลังโหลด...</span>
            </div>
          ) : (
            <>
              {/* Mobile Card View (< 768px) */}
              <div className="md:hidden space-y-3 p-4">
                {users.map((user) => (
                  <div
                    key={user.id}
                    className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    {/* User Info */}
                    <div className="flex items-start mb-3">
                      <div className="flex-shrink-0 h-12 w-12">
                        <div className="h-12 w-12 rounded-full bg-gray-300 flex items-center justify-center">
                          <FiUsers className="h-7 w-7 text-gray-600" />
                        </div>
                      </div>
                      <div className="ml-3 flex-1">
                        <div className="text-sm font-semibold text-gray-900">
                          {user.firstName && user.lastName
                            ? `${user.firstName} ${user.lastName}`
                            : 'ไม่ระบุชื่อ'
                          }
                        </div>
                        <div className="text-xs text-gray-500 flex items-center mt-1">
                          <FiMail className="h-3 w-3 mr-1 flex-shrink-0" />
                          <span className="truncate">{user.email}</span>
                        </div>
                        {user.phone && (
                          <div className="text-xs text-gray-500 flex items-center mt-1">
                            <FiPhone className="h-3 w-3 mr-1 flex-shrink-0" />
                            {user.phone}
                          </div>
                        )}
                        {/* User Category */}
                        {user.userCategory && (
                          <div className="text-xs text-gray-700 flex items-center mt-1">
                            <span className="mr-1">{getUserCategoryIcon(user.userCategory)}</span>
                            <span className="font-medium">{getUserCategoryLabel(user.userCategory)}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Role and Status Badges */}
                    <div className="flex items-center space-x-2 mb-3">
                      <RoleBadge role={user.role} />
                      <StatusBadge status={user.status} />
                    </div>

                    {/* Dates Info */}
                    <div className="text-xs text-gray-600 space-y-1 mb-3 pb-3 border-b border-gray-100">
                      <div>
                        สมัคร: <span className="font-medium">{new Date(user.createdAt).toLocaleDateString('th-TH')}</span>
                      </div>
                      <div>
                        Login ล่าสุด:{' '}
                        <span className="font-medium">
                          {user.lastLoginAt
                            ? new Date(user.lastLoginAt).toLocaleDateString('th-TH')
                            : 'ยังไม่เคยเข้าสู่ระบบ'
                          }
                        </span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center justify-end space-x-2">
                      {user.status === 'ACTIVE' ? (
                        <button
                          onClick={() => handleUserAction(user.id, 'deactivate')}
                          className="flex items-center space-x-1 text-xs text-yellow-600 hover:bg-yellow-50 px-3 py-1.5 rounded"
                          title="ปิดใช้งาน"
                        >
                          <FiUserX className="h-4 w-4" />
                          <span>ปิดใช้งาน</span>
                        </button>
                      ) : (
                        <button
                          onClick={() => handleUserAction(user.id, 'activate')}
                          className="flex items-center space-x-1 text-xs text-green-600 hover:bg-green-50 px-3 py-1.5 rounded"
                          title="เปิดใช้งาน"
                        >
                          <FiUserCheck className="h-4 w-4" />
                          <span>เปิดใช้งาน</span>
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setEditingUser(user);
                          setShowUserModal(true);
                        }}
                        className="flex items-center space-x-1 text-xs text-blue-600 hover:bg-blue-50 px-3 py-1.5 rounded"
                        title="แก้ไข"
                      >
                        <FiEdit2 className="h-4 w-4" />
                        <span>แก้ไข</span>
                      </button>
                      <button
                        onClick={() => handleUserAction(user.id, 'delete', user)}
                        className="flex items-center space-x-1 text-xs text-red-600 hover:bg-red-50 px-3 py-1.5 rounded"
                        title="ลบ"
                      >
                        <FiTrash2 className="h-4 w-4" />
                        <span>ลบ</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View (>= 768px) */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          className="rounded"
                          checked={selectedUsers.length === users.length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedUsers(users.map(u => u.id));
                            } else {
                              setSelectedUsers([]);
                            }
                          }}
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ชื่อ-นามสกุล
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        อีเมล
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ประเภทผู้ใช้
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        บทบาท
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        สถานะ
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        การจัดการ
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            className="rounded"
                            checked={selectedUsers.includes(user.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedUsers(prev => [...prev, user.id]);
                              } else {
                                setSelectedUsers(prev => prev.filter(id => id !== user.id));
                              }
                            }}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                <FiUsers className="h-6 w-6 text-gray-600" />
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {user.firstName && user.lastName
                                  ? `${user.firstName} ${user.lastName}`
                                  : 'ไม่ระบุชื่อ'
                                }
                              </div>
                              {user.phone && (
                                <div className="text-xs text-gray-500 flex items-center">
                                  <FiPhone className="h-3 w-3 mr-1" />
                                  {user.phone}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 flex items-center">
                            <FiMail className="h-4 w-4 mr-1 text-gray-400" />
                            {user.email}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {user.userCategory ? (
                            <div className="flex items-center">
                              <span className="text-lg mr-1">{getUserCategoryIcon(user.userCategory)}</span>
                              <span className="text-sm text-gray-900">{getUserCategoryLabel(user.userCategory)}</span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">ไม่ระบุ</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <RoleBadge role={user.role} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <StatusBadge status={user.status} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            {user.status === 'ACTIVE' ? (
                              <button
                                onClick={() => handleUserAction(user.id, 'deactivate')}
                                className="text-yellow-600 hover:text-yellow-900 p-1"
                                title="ปิดใช้งาน"
                              >
                                <FiUserX className="h-4 w-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => handleUserAction(user.id, 'activate')}
                                className="text-green-600 hover:text-green-900 p-1"
                                title="เปิดใช้งาน"
                              >
                                <FiUserCheck className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              onClick={() => {
                                setEditingUser(user);
                                setShowUserModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-900 p-1"
                              title="แก้ไข"
                            >
                              <FiEdit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleUserAction(user.id, 'delete', user)}
                              className="text-red-600 hover:text-red-900 p-1"
                              title="ลบ"
                            >
                              <FiTrash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => loadUsers(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      ก่อนหน้า
                    </button>
                    <button
                      onClick={() => loadUsers(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      ถัดไป
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        แสดง <span className="font-medium">{((currentPage - 1) * 20) + 1}</span> ถึง{' '}
                        <span className="font-medium">{Math.min(currentPage * 20, totalUsers)}</span> จาก{' '}
                        <span className="font-medium">{totalUsers}</span> รายการ
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() => loadUsers(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          ก่อนหน้า
                        </button>
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                          return (
                            <button
                              key={page}
                              onClick={() => loadUsers(page)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                page === currentPage
                                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        })}
                        <button
                          onClick={() => loadUsers(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          ถัดไป
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Empty State */}
        {!loading && users.length === 0 && (
          <div className="text-center py-12">
            <FiUsers className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">ไม่พบผู้ใช้</h3>
            <p className="mt-1 text-sm text-gray-500">ลองปรับเปลี่ยนเงื่อนไขการค้นหาหรือกรอง</p>
          </div>
        )}

        {/* Edit User Modal */}
        <EditUserModal
          isOpen={showUserModal}
          onClose={() => {
            setShowUserModal(false);
            setEditingUser(null);
          }}
          user={editingUser}
          onSuccess={() => loadUsers(currentPage)}
        />

        {/* Confirm Dialog */}
        {ConfirmDialog}
        {AlertDialog}
      </div>
    </AdminLayout>
  );
};

export default UserManagement;